import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './Modal.css';

const Modal = ({ isOpen, onClose, type }) => {
  const { t } = useTranslation();
  const [selectedFaqSection, setSelectedFaqSection] = useState(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setSelectedFaqSection(null);
    } else {
      document.body.style.overflow = 'unset';
      setSelectedFaqSection(null);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const renderContent = () => {
    switch (type) {
      case 'contact':
        return (
          <div className="modal-content-body">
            <h2 className="modal-title">{t('footer.contactUs')}</h2>
            <div className="modal-section">
              <h3>{t('modal.contact.address')}</h3>
              <p>Lipová 25</p>
              <p>415 01 Teplice</p>
              <p>Czech Republic</p>
            </div>
            <div className="modal-section">
              <h3>{t('modal.contact.email')}</h3>
              <p>
                <a href="mailto:info@kehila-teplice.cz" className="modal-link">
                  info@kehila-teplice.cz
                </a>
              </p>
            </div>
            <div className="modal-section">
              <h3>{t('modal.contact.phone')}</h3>
              <p>
                <a href="tel:+420417635073" className="modal-link">
                  +420 417 635 073
                </a>
              </p>
            </div>
          </div>
        );

      case 'bank':
        return (
          <div className="modal-content-body">
            <h2 className="modal-title">{t('footer.bankDetails')}</h2>
            <div className="modal-section">
              <h3>{t('modal.bank.bankInformation')}</h3>
              <div className="modal-info-grid">
                <div className="modal-info-item">
                  <span className="modal-info-label">{t('modal.bank.bankName')}</span>
                  <span className="modal-info-value">Česká spořitelna a.s.pobočka Teplice¨</span>
                </div>
                <div className="modal-info-item">
                  <span className="modal-info-label">{t('modal.bank.address')}</span>
                  <span className="modal-info-value">Benešovo nám.421/1</span>
                </div>
                <div className="modal-info-item">
                  <span className="modal-info-label">{t('modal.bank.accountNumber')}</span>
                  <span className="modal-info-value">000000-1061067369/0800</span>
                </div>
                <div className="modal-info-item">
                  <span className="modal-info-label">{t('modal.bank.iban')}</span>
                  <span className="modal-info-value">CZ5108000000001061067369</span>
                </div>
                <div className="modal-info-item">
                  <span className="modal-info-label">{t('modal.bank.swiftBic')}</span>
                  <span className="modal-info-value">GIBACZPX</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'faq':
        const faqSections = [
          {
            id: 'general',
            titleKey: 'faq.sections.general',
            contentKey: 'faq.content.general'
          },
          {
            id: 'membership',
            titleKey: 'faq.sections.membership',
            contentKey: 'faq.content.membership'
          },
          {
            id: 'services',
            titleKey: 'faq.sections.services',
            contentKey: 'faq.content.services'
          },
          {
            id: 'support',
            titleKey: 'faq.sections.support',
            contentKey: 'faq.content.support'
          }
        ];

        if (selectedFaqSection) {
          const section = faqSections.find(s => s.id === selectedFaqSection);
          return (
            <div className="modal-content-body">
              <button
                className="modal-back-btn"
                onClick={() => setSelectedFaqSection(null)}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {t('faq.backToSections')}
              </button>
              <h2 className="modal-title">{t(section?.titleKey)}</h2>
              <div className="modal-faq-content">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {t(section?.contentKey)}
                </ReactMarkdown>
              </div>
            </div>
          );
        }

        return (
          <div className="modal-content-body">
            <h2 className="modal-title">{t('footer.faq')}</h2>
            <div className="modal-faq-sections">
              {faqSections.map((section) => (
                <button
                  key={section.id}
                  className="modal-faq-section-btn"
                  onClick={() => setSelectedFaqSection(section.id)}
                >
                  <span className="modal-faq-section-title">{t(section.titleKey)}</span>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose} aria-label="Close modal">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {renderContent()}
      </div>
    </div>
  );
};

export default Modal;